import { z } from 'zod';
import type { ClockWindow } from './pricing-windows.js';
import { vehicleClasses } from './vehicle-class.js';

/**
 * Plano de mensalista do catálogo do lote (#396).
 *
 * Dois eixos no mesmo plano porque as placas vendem nos dois: a Tarifa Avulso
 * por classe (`Carro R$ 160,00 · Moto R$ 60,00 · Bicicleta R$ 30,00`) e a EV
 * Park por período (`diurno / noturno / integral`). Um lote pode vender "moto
 * noturno" sem precisar de duas tabelas.
 */
export const monthlyPlanInputSchema = z
  .object({
    name: z.string().min(1).max(80),
    /** Classe atendida. Ausente vale para qualquer uma. */
    vehicleClass: z.enum(vehicleClasses).nullish(),
    /**
     * Faixa de horas coberta, no relógio do lote. As duas ausentes significam
     * integral. Uma só nunca: meia faixa é um campo que o dono abriu e não
     * terminou, e a cobertura sairia errada sem ninguém ver (#411).
     */
    coverageStartHour: z.number().int().min(0).max(23).nullish(),
    coverageEndHour: z.number().int().min(0).max(23).nullish(),
    /** Mensalidade. Nunca zero: plano de graça não é plano (#411). */
    amountCents: z.number().int().positive(),
  })
  .refine(
    (v) =>
      (v.coverageStartHour == null && v.coverageEndHour == null) ||
      (v.coverageStartHour != null && v.coverageEndHour != null),
    {
      message: 'Informe as duas horas da faixa, ou nenhuma para cobertura integral.',
      path: ['coverageEndHour'],
    },
  )
  .refine(
    // Só vale quando existe faixa: integral é as duas ausentes, e compará-las
    // entre si reprovaria justamente o plano mais comum.
    (v) =>
      v.coverageStartHour == null ||
      v.coverageEndHour == null ||
      v.coverageStartHour !== v.coverageEndHour,
    {
      message: 'A faixa precisa ter início e fim diferentes.',
      path: ['coverageEndHour'],
    },
  );

export type MonthlyPlanInput = z.infer<typeof monthlyPlanInputSchema>;

/**
 * Faixa coberta pela mensalidade, do jeito que `computePrice` espera (#396).
 *
 * As duas horas vêm juntas ou não vêm: meia faixa não descreve janela nenhuma,
 * e tratá-la como cobertura parcial cobraria o mensalista por um dado pela
 * metade. `undefined` significa cobertura integral.
 *
 * Vive aqui, e não no fechamento do ticket, porque a página pública, a travada
 * do valor e o recibo precisam da mesma leitura — divergir aqui é mostrar um
 * número ao cliente e cobrar outro.
 */
export const monthlyCoverageOf = (t: {
  monthlyCoverageStartHour: number | null;
  monthlyCoverageEndHour: number | null;
}): ClockWindow | undefined =>
  t.monthlyCoverageStartHour !== null && t.monthlyCoverageEndHour !== null
    ? { startHour: t.monthlyCoverageStartHour, endHour: t.monthlyCoverageEndHour }
    : undefined;

/** Rótulo da cobertura, na língua de quem lê a placa. */
export const coverageLabel = (
  startHour: number | null | undefined,
  endHour: number | null | undefined,
): string =>
  startHour == null || endHour == null
    ? 'Dia inteiro'
    : `${String(startHour).padStart(2, '0')}h às ${String(endHour).padStart(2, '0')}h`;
